import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, FormControlLabel,
  IconButton, Paper, Slider, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import {
  uploadTestImages, listTestImages, fetchImageBlob, testImageThumbPath, testImagePath,
  clearTestImages, runDetection, listResults, resultImageThumbPath, resultImagePath, clearResults,
  listTrainedModels,
} from '../../api/annotatorDetectionTest';

/** Component that fetches an image via Authorization header and displays it. */
function AuthImage({ url, alt, sx, onClick }: {
  url: string; alt: string; sx?: Record<string, any>; onClick?: (e: React.MouseEvent) => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const urlRef = useRef(url);

  useEffect(() => {
    urlRef.current = url;
    let objectUrl: string | null = null;
    fetchImageBlob(url).then((u) => {
      if (urlRef.current === url) { objectUrl = u; setSrc(u); }
      else URL.revokeObjectURL(u);
    }).catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url]);

  if (!src) return <Box sx={{ ...sx, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={16} /></Box>;
  return <Box component="img" src={src} alt={alt} sx={sx} onClick={onClick} />;
}

export default function AnnotatorDetectionTestPage() {
  const { projectId } = useParams();
  const pid = Number(projectId);

  const [images, setImages] = useState<string[]>([]);
  const [results, setResults] = useState<string[]>([]);
  const [confidence, setConfidence] = useState(0.25);
  const [running, setRunning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);  // API path to fetch
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);  // blob object URL
  const [dragOver, setDragOver] = useState(false);
  const [projectCategory, setProjectCategory] = useState('');
  const [allClasses, setAllClasses] = useState<{ index: number; name: string }[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [hasModel, setHasModel] = useState(false);

  const refresh = useCallback(async () => {
    const [imgRes, resRes] = await Promise.all([listTestImages(pid), listResults(pid)]);
    setImages(imgRes.images);
    setResults(resRes.results);
  }, [pid]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    listTrainedModels(pid).then((res) => {
      setProjectCategory(res.category);
      setAllClasses(res.classes);
      setSelectedClasses(res.classes.map((c: any) => c.index));
      setHasModel(res.has_model);
    }).catch(() => {});
  }, [pid]);

  // Load full-size image for lightbox via auth header
  useEffect(() => {
    if (!lightboxUrl) { setLightboxSrc(null); return; }
    let objectUrl: string | null = null;
    fetchImageBlob(lightboxUrl).then((u) => { objectUrl = u; setLightboxSrc(u); }).catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [lightboxUrl]);

  async function handleUpload(files: FileList | File[]) {
    setError('');
    setUploading(true);
    try {
      const arr = Array.from(files);
      await uploadTestImages(pid, arr);
      setInfo(`Uploaded ${arr.length} image(s)`);
      await refresh();
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleRun() {
    setError('');
    setInfo('');
    setRunning(true);
    try {
      const classFilter = selectedClasses.length === allClasses.length ? undefined : selectedClasses;
      const res = await runDetection(pid, confidence, projectCategory, classFilter);
      setInfo(`Detection complete: ${res.successful} processed, ${res.failed} failed (${res.category})`);
      await refresh();
    } catch (e: any) {
      setError(e.message || 'Detection failed');
    } finally {
      setRunning(false);
    }
  }

  async function handleClearImages() {
    setError('');
    await clearTestImages(pid);
    setInfo('Test images cleared');
    await refresh();
  }

  async function handleClearResults() {
    setError('');
    await clearResults(pid);
    setInfo('Results cleared');
    await refresh();
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleUpload(e.dataTransfer.files);
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Detection Test</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

      {/* Upload area */}
      <Paper
        sx={{
          p: 3, mb: 3, textAlign: 'center',
          border: '2px dashed', borderColor: dragOver ? 'primary.main' : 'divider',
          bgcolor: dragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = 'image/*';
          input.onchange = () => { if (input.files?.length) handleUpload(input.files); };
          input.click();
        }}
      >
        {uploading ? <CircularProgress size={24} /> : (
          <>
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">Drop test images here or click to browse</Typography>
          </>
        )}
      </Paper>

      {/* Test images */}
      {images.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2">Test Images ({images.length})</Typography>
            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={handleClearImages}>Clear All</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {images.map((name) => (
              <AuthImage
                key={name}
                url={testImageThumbPath(pid, name)}
                alt={name}
                sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); setLightboxUrl(testImagePath(pid, name)); }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Run detection controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Run Detection</Typography>
          {projectCategory && <Chip label={projectCategory} size="small" color={hasModel ? 'success' : 'default'} variant="outlined" />}
        </Box>
        {!hasModel && projectCategory && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>No trained model found for "{projectCategory}". Train the model first.</Alert>
        )}
        {allClasses.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Classes:</Typography>
              <Button size="small" onClick={() => setSelectedClasses(allClasses.map(c => c.index))} sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}>All</Button>
              <Button size="small" onClick={() => setSelectedClasses([])} sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}>None</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
              {allClasses.map((c) => (
                <FormControlLabel
                  key={c.index}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedClasses.includes(c.index)}
                      onChange={(e) => {
                        setSelectedClasses((prev) =>
                          e.target.checked ? [...prev, c.index] : prev.filter((i) => i !== c.index)
                        );
                      }}
                      disabled={running}
                    />
                  }
                  label={<Typography variant="body2">{c.name}</Typography>}
                  sx={{ mr: 1.5 }}
                />
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">Confidence Threshold: {confidence.toFixed(2)}</Typography>
            <Slider
              value={confidence}
              onChange={(_, v) => setConfidence(v as number)}
              min={0.05} max={0.95} step={0.05}
              size="small"
              disabled={running}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleRun}
            disabled={running || images.length === 0 || !hasModel || selectedClasses.length === 0}
          >
            {running ? 'Running...' : 'Run Detection'}
          </Button>
        </Box>
      </Paper>

      {/* Results gallery */}
      {results.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2">Results ({results.length})</Typography>
            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={handleClearResults}>Clear Results</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {results.map((name) => (
              <Box
                key={name}
                sx={{ cursor: 'pointer' }}
                onClick={() => setLightboxUrl(resultImagePath(pid, name))}
              >
                <AuthImage
                  url={resultImageThumbPath(pid, name)}
                  alt={name}
                  sx={{ width: 280, height: 210, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                />
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280, display: 'block' }}>
                  {name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightboxUrl} onClose={() => setLightboxUrl(null)} maxWidth="xl">
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
          {lightboxSrc ? (
            <Box component="img" src={lightboxSrc} alt="Detection result"
              sx={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
            />
          ) : (
            <Box sx={{ width: 400, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
